// src/Components/ToastProvider.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { Snackbar, Alert, Slide, CircularProgress, Box } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import Warning from "@mui/icons-material/Warning";

// Slide transition from left
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const ToastContext = createContext();

// Custom icon mapping for different severities
const getSeverityIcon = (severity) => {
  switch (severity) {
    case "success":
      return <CheckCircle fontSize="small" />;
    case "error":
      return <Error fontSize="small" />;
    case "warning":
      return <Warning fontSize="small" />;
    case "info":
      return <Info fontSize="small" />;
    default:
      return null;
  }
};

export function AnimatedToastProvider({ children }) {
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
    autoHideDuration: 3000,
    isLoading: false,
  });

  const timeoutRef = useRef(null);

  // Clear any existing auto-hide timeout
  const clearAutoHide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Close toast
  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") return;
      clearAutoHide();
      setToast((prev) => ({ ...prev, open: false }));
    },
    [clearAutoHide],
  );

  // Show a new toast or update existing one
  const showToast = useCallback(
    (message, severity = "info", options = {}) => {
      const {
        autoHideDuration = 3000,
        isLoading = false,
        persist = false,
      } = options;

      // Clear any existing auto-hide timeout
      clearAutoHide();

      // If persist is true, don't auto-hide (useful for loading states)
      const finalAutoHideDuration = persist ? null : autoHideDuration;

      setToast({
        open: true,
        message,
        severity,
        autoHideDuration: finalAutoHideDuration,
        isLoading,
      });

      // Set auto-hide timeout if duration is provided
      if (finalAutoHideDuration !== null && finalAutoHideDuration > 0) {
        timeoutRef.current = setTimeout(() => {
          setToast((prev) => ({ ...prev, open: false }));
          timeoutRef.current = null;
        }, finalAutoHideDuration);
      }

      // Return a controller object for updating this toast
      return {
        update: (newMessage, newSeverity, newOptions = {}) => {
          const {
            autoHideDuration: newDuration = 2000,
            isLoading: newIsLoading = false,
          } = newOptions;

          clearAutoHide();

          setToast((prev) => ({
            ...prev,
            message: newMessage,
            severity: newSeverity,
            isLoading: newIsLoading,
            autoHideDuration: newDuration,
            open: true,
          }));

          // Set new auto-hide timeout
          if (newDuration !== null && newDuration > 0) {
            timeoutRef.current = setTimeout(() => {
              setToast((prev) => ({ ...prev, open: false }));
              timeoutRef.current = null;
            }, newDuration);
          }
        },
        dismiss: () => {
          clearAutoHide();
          setToast((prev) => ({ ...prev, open: false }));
        },
      };
    },
    [clearAutoHide],
  );

  // Convenience method for async operations (Promise-based toast)
  const showPromiseToast = useCallback(
    async (promise, options = {}) => {
      const {
        loadingMessage = "در حال انجام...",
        successMessage = "عملیات با موفقیت انجام شد",
        errorMessage = "خطا در انجام عملیات",
        loadingSeverity = "info",
        successSeverity = "success",
        errorSeverity = "error",
      } = options;

      // Show loading toast
      const toastController = showToast(loadingMessage, loadingSeverity, {
        isLoading: true,
        persist: true,
        autoHideDuration: null,
      });

      try {
        const result = await promise;
        // Update to success
        toastController.update(successMessage, successSeverity, {
          autoHideDuration: 2000,
        });
        return result;
      } catch (error) {
        // Update to error
        const finalErrorMessage =
          typeof errorMessage === "function"
            ? errorMessage(error)
            : errorMessage;
        toastController.update(finalErrorMessage, errorSeverity, {
          autoHideDuration: 3000,
        });
        throw error;
      }
    },
    [showToast],
  );

  // Simple success toast
  const showSuccess = useCallback(
    (message, autoHideDuration = 2000) => {
      return showToast(message, "success", { autoHideDuration });
    },
    [showToast],
  );

  // Simple error toast
  const showError = useCallback(
    (message, autoHideDuration = 3000) => {
      return showToast(message, "error", { autoHideDuration });
    },
    [showToast],
  );

  // Simple info toast
  const showInfo = useCallback(
    (message, autoHideDuration = 3000) => {
      return showToast(message, "info", { autoHideDuration });
    },
    [showToast],
  );

  // Simple warning toast
  const showWarning = useCallback(
    (message, autoHideDuration = 3000) => {
      return showToast(message, "warning", { autoHideDuration });
    },
    [showToast],
  );

  const value = {
    showToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showPromiseToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={null} // Controlled manually
        sx={{
          "& .MuiSnackbar-root": {
            direction: "rtl",
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={toast.severity}
          variant="filled"
          icon={toast.isLoading ? false : getSeverityIcon(toast.severity)}
          sx={{
            width: "100%",
            fontFamily: "Dana",
            fontSize: "14px",
            borderRadius: 2,
            boxShadow: 3,
            direction: "rtl",
            alignItems: "center",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          {toast.isLoading && (
            <CircularProgress
              size={16}
              thickness={5}
              sx={{
                color: "inherit",
                mr: 1,
              }}
            />
          )}
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
          >
            {toast.message}
          </Box>
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
